import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
} from '@mui/material';
import {
  Update,
  CheckCircle,
  ArrowBack,
  Article,
  AttachMoney,
  Group,
  School,
  Work,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

const UKImmigrationChanges2024 = () => {
  const theme = useTheme();

  const keyChanges = [
    {
      title: 'Skilled Worker Visa Updates',
      description: 'New salary thresholds and occupation codes for skilled workers.',
      icon: Work,
      details: [
        'Minimum salary requirement increased to £38,700',
        'Updated Shortage Occupation List',
        'Changes to experience requirements',
      ],
    },
    {
      title: 'Student Visa Modifications',
      description: 'Revised policies for international students and graduates.',
      icon: School,
      details: [
        'Updates to financial requirements',
        'Changes to dependent policies',
        'Modified study duration rules',
      ],
    },
    {
      title: 'Family Visa Amendments',
      description: 'New requirements for family reunification visas.',
      icon: Group,
      details: [
        'Updated minimum income requirements',
        'Changes to English language requirements',
        'Modified accommodation criteria',
      ],
    },
    {
      title: 'Fee Structure Changes',
      description: 'Revised application fees and healthcare surcharge.',
      icon: AttachMoney,
      details: [
        'Immigration Health Surcharge increases',
        'New visa application fees',
        'Priority service cost updates',
      ],
    },
  ];

  return (
    <Box sx={{ py: { xs: 6, md: 8 } }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ mb: 6 }}>
          <Button
            component={RouterLink}
            to="/"
            startIcon={<ArrowBack />}
            sx={{ mb: 3 }}
          >
            Back to Home
          </Button>
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 700,
              mb: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            UK Immigration Rule Changes 2024
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            <Chip
              icon={<Update />}
              label="March 15, 2024"
              sx={{ backgroundColor: `${theme.palette.primary.main}15` }}
            />
            <Chip
              icon={<Article />}
              label="Policy Update"
              sx={{ backgroundColor: `${theme.palette.secondary.main}15` }}
            />
          </Box>
          <Typography
            variant="h5"
            color="textSecondary"
            sx={{ mb: 4, maxWidth: 800 }}
          >
            Comprehensive overview of the latest changes to UK immigration policies and their impact on various visa categories.
          </Typography>
          <Divider sx={{ my: 4 }} />
        </Box>

        {/* Main Content */}
        <Grid container spacing={4}>
          {keyChanges.map((change, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: '100%',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <change.icon
                    sx={{
                      fontSize: 40,
                      color: theme.palette.primary.main,
                      mr: 2,
                    }}
                  />
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {change.title}
                  </Typography>
                </Box>
                <Typography color="textSecondary" paragraph>
                  {change.description}
                </Typography>
                <List>
                  {change.details.map((detail, idx) => (
                    <ListItem key={idx} sx={{ px: 0 }}>
                      <ListItemIcon>
                        <CheckCircle color="primary" sx={{ fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText primary={detail} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Call to Action */}
        <Paper
          sx={{
            mt: 6,
            p: 4,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.primary.light}15)`,
            borderRadius: 2,
          }}
        >
          <Typography variant="h5" gutterBottom>
            Need Help Understanding These Changes?
          </Typography>
          <Typography paragraph color="textSecondary">
            Our immigration experts are here to guide you through these new changes and help you understand how they affect your visa application.
          </Typography>
          <Button
            component={RouterLink}
            to="/consultation"
            variant="contained"
            color="primary"
            size="large"
            sx={{ mt: 2 }}
          >
            Book a Consultation
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default UKImmigrationChanges2024; 